import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import api, { setOnRefreshFail, setImpersonateRole } from '../api/client';
import { ROLE_LEVEL } from '../utils/constants';
import {
  applyAuthResponse,
  loginWithPasswordRequest,
  sendOtpRequest,
  verifyOtpRequest,
  trustLoginRequest,
  logoutRequest,
  refreshSessionRequest,
} from './authApi';

const AuthContext = createContext(null);
const IMPERSONATE_KEY = 'lumyn_impersonate_role';

function useImpersonation(user) {
  const [impersonatingRole, setImpersonatingRoleState] = useState(
    () => sessionStorage.getItem(IMPERSONATE_KEY) || null,
  );

  // Sync impersonation header on mount / change
  useEffect(() => {
    if (impersonatingRole) setImpersonateRole(impersonatingRole);
  }, [impersonatingRole]);

  const canImpersonate = useCallback(() => {
    if (!user) return false;
    return (ROLE_LEVEL[user.role] || 0) >= (ROLE_LEVEL['org_admin'] || 99);
  }, [user]);

  const startImpersonation = useCallback((role) => {
    if (!role || role === user?.role) {
      // Selecting own role exits impersonation
      setImpersonatingRoleState(null);
      sessionStorage.removeItem(IMPERSONATE_KEY);
      setImpersonateRole(null);
      return;
    }
    setImpersonatingRoleState(role);
    sessionStorage.setItem(IMPERSONATE_KEY, role);
    setImpersonateRole(role);
  }, [user]);

  const stopImpersonation = useCallback(() => {
    setImpersonatingRoleState(null);
    sessionStorage.removeItem(IMPERSONATE_KEY);
    setImpersonateRole(null);
  }, []);

  return { impersonatingRole, canImpersonate, startImpersonation, stopImpersonation, setImpersonatingRoleState };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const {
    impersonatingRole, canImpersonate, startImpersonation, stopImpersonation,
    setImpersonatingRoleState,
  } = useImpersonation(user);

  const isAuthenticated = !!user;
  const effectiveRole = impersonatingRole || user?.role;

  // Load the current user's effective permission set (reflects impersonation,
  // since the impersonation header is sent on this request too).
  useEffect(() => {
    if (!user) { setPermissions([]); return undefined; }
    let cancelled = false;
    api.get('/roles/me')
      .then((res) => { if (!cancelled) setPermissions(res.data?.permissions || []); })
      .catch(() => { if (!cancelled) setPermissions([]); });
    return () => { cancelled = true; };
  }, [user, impersonatingRole]);

  const hasRole = useCallback((minRole) => {
    if (!effectiveRole) return false;
    return (ROLE_LEVEL[effectiveRole] || 0) >= (ROLE_LEVEL[minRole] || 99);
  }, [effectiveRole]);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (effectiveRole === 'super_admin' || effectiveRole === 'admin') return true;
    return permissions.includes(perm);
  }, [user, effectiveRole, permissions]);

  const isSuperAdmin = useCallback(() => hasRole('super_admin'), [hasRole]);
  const isOrgAdmin = useCallback(() => hasRole('org_admin'), [hasRole]);
  const isEditor = useCallback(() => hasRole('editor'), [hasRole]);

  const loginWithPassword = useCallback(async (email, password) => {
    const data = await loginWithPasswordRequest(email, password);
    const { user: u, org: o } = applyAuthResponse(data);
    setUser(u); setOrg(o);
    return data;
  }, []);

  const sendOtp = useCallback((email) => sendOtpRequest(email), []);

  const verifyOtp = useCallback(async (email, otpCode, opts) => {
    const data = await verifyOtpRequest(email, otpCode, opts);
    const { user: u, org: o } = applyAuthResponse(data);
    setUser(u); setOrg(o);
    return data;
  }, []);

  const trustLogin = useCallback(async (email) => {
    const data = await trustLoginRequest(email);
    const { user: u, org: o } = applyAuthResponse(data);
    setUser(u); setOrg(o);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setOrg(null);
    setImpersonatingRoleState(null);
  }, [setImpersonatingRoleState]);

  const refreshSession = useCallback(async () => {
    const result = await refreshSessionRequest();
    if (result) { setUser(result.user); setOrg(result.org); }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refreshSession();
    setOnRefreshFail(() => {
      setUser(null);
      setOrg(null);
    });
  }, [refreshSession]);

  const value = useMemo(
    () => ({
      user, org, isAuthenticated, isLoading,
      hasRole, hasPermission, permissions, isSuperAdmin, isOrgAdmin, isEditor,
      loginWithPassword, sendOtp, verifyOtp, trustLogin, logout, refreshSession,
      impersonatingRole, canImpersonate, startImpersonation, stopImpersonation,
    }),
    [
      user, org, isAuthenticated, isLoading,
      hasRole, hasPermission, permissions, isSuperAdmin, isOrgAdmin, isEditor,
      loginWithPassword, sendOtp, verifyOtp, trustLogin, logout, refreshSession,
      impersonatingRole, canImpersonate, startImpersonation, stopImpersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
